import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";

export default function AccuracyGraph({ data, dataKey }: { data: any[], dataKey:string }) {
  const effectiveKey = typeof dataKey === "string" ? dataKey.trim() : "";
  if (!effectiveKey) {
    return (
      <div style={{ marginTop: "30px" }}>
        <h3>Metric Over Time</h3>
        <p style={{ color: "var(--color-text-muted)" }}>Select a metric to render the chart.</p>
      </div>
    );
  }

  const raw = Array.isArray(data) ? data : [];
  if (!raw.length) {
    return (
      <div style={{ marginTop: "30px" }}>
        <h3>{effectiveKey} Over Time</h3>
        <p style={{ color: "var(--color-text-muted)" }}>No data to render yet.</p>
      </div>
    );
  }

  // Normalize + keep only rows with valid Timestamp and metric
  const points = raw
    .filter((e) => e && e.Timestamp)
    .map((e) => {
      const t = new Date(e.Timestamp).toISOString();
      const rawVal = e[effectiveKey];
      const val =
        typeof rawVal === "number" ? rawVal : Number.isFinite(Number(rawVal)) ? Number(rawVal) : NaN;

      return { Timestamp: t, [effectiveKey]: val };
    })
    .filter((p) => Number.isFinite(p[effectiveKey] as number));

  if (!points.length) {
    return (
      <div style={{ marginTop: "30px" }}>
        <h3>{effectiveKey} Over Time</h3>
        <p style={{ color: "var(--color-text-muted)" }}>
          No valid values found for "{effectiveKey}".
        </p>
      </div>
    );
  }

  points.sort((a, b) => new Date(a.Timestamp).getTime() - new Date(b.Timestamp).getTime());

  // If there are lots of points, aggregate per month for readability.
  const MAX_POINTS = 60;
  let chartData = points;
  if (points.length > MAX_POINTS) {
    const byMonth = new Map<
      string,
      { sum: number; count: number; monthStartISO: string }
    >();

    for (const p of points) {
      const value = p[effectiveKey] as number;
      const d = new Date(p.Timestamp);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      const monthStartISO = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString();
      const prev = byMonth.get(key);
      if (!prev) {
        byMonth.set(key, { sum: value, count: 1, monthStartISO });
      } else {
        prev.sum += value;
        prev.count += 1;
      }
    }

    chartData = Array.from(byMonth.entries())
      .sort((a, b) => new Date(a[1].monthStartISO).getTime() - new Date(b[1].monthStartISO).getTime())
      .map(([, v]) => ({
        Timestamp: v.monthStartISO,
        [effectiveKey]: v.sum / Math.max(1, v.count),
      }));
  }

  return (
    <div style={{ marginTop: "var(--space-5)" }}>
      <h3>{effectiveKey} Over Time</h3>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
          <CartesianGrid stroke="var(--border-light)" vertical={false} />
          <XAxis
            dataKey="Timestamp"
            tickFormatter={(val) => new Date(val).toLocaleDateString()}
            tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
            tickLine={false}
            axisLine={{ stroke: "var(--border)" }}
          />
          <YAxis
            domain={['auto', 'auto']}
            tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip />
          <Line
            type="monotone"
            dataKey={effectiveKey}
            stroke="var(--primary)"
            strokeWidth={2}
            dot={{ r: 3, fill: "var(--primary)", strokeWidth: 0 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
